import { signal, computed } from "@preact/signals";
import {
  HlcClock,
  newId,
  cents,
  type MenuSnapshot,
  type MenuItem,
  type User,
  type SizeVariant,
  type MenuCategoryId,
  type OrderLineId,
  type OrderLineModifier,
  type TableId,
  type EventId,
  type Order,
  type OrderStatus,
  type PaymentId,
  type PaymentMethod,
  type Cents,
  type EventEnvelope,
  type TablesConfig,
  type VenueId,
  isFullyPaid,
  isTableAllowed,
  tablesConfigured,
  isWithinSchedule,
  soldCounts as computeSoldCounts,
  waitStats as computeWaitStats,
  type ConfigKind,
  type PrintRequested,
} from "@orderman/shared";
import { settings, updateSettings, getDeviceId, venuePinned, deviceUnassigned } from "./config.js";
import { currentUser, setUsers, logout } from "./auth.js";
import { loadCachedMenu, refreshMenu } from "./menu.js";
import { enqueue, pending } from "./outbox.js";
import { flush, setCloudFallback, cloudOnline, localOnline } from "./sync.js";
import { mirrorOrders, initMirror, resetMirror } from "./mirror.js";
import { ordersPut, ordersGetAll, kvGet, kvSet, kvDelete, kvKeys, ordersClear, outboxClear } from "./idb.js";
import { STATE_VENUE_KEY, venueAction, isVenueScopedKey } from "./venueReset.js";
import * as Draft from "./draft.js";

/** Persistierter Datensatz einer Bestellung dieses Geräts ("Meine Bons"). */
export interface OrderRecord {
  id: string;
  draft: Draft.Draft;
  openedAtMs: number;
  updatedAtMs: number;
  /** Vom Server vergebene Bon-Nummer (best effort, wenn online abgeglichen). */
  seqNo?: number;
  serverStatus?: OrderStatus;
}

export type Screen = "order" | "bons" | "admin";

/** Globaler UI-Zustand der PWA (Signals). */
export const menu = signal<MenuSnapshot | null>(null);
export const draft = signal<Draft.Draft | null>(null);
export const activeCategory = signal<MenuCategoryId | null>(null);
export const menuLoading = signal(false);
export const screen = signal<Screen>("order");
/** Unteransicht im Bestell-Screen: Tisch-Keypad, laufende Bestellung oder Abrechnung. */
export const tableView = signal<"pad" | "order" | "pay">("pad");
export const myOrders = signal<OrderRecord[]>([]);
/** Aktuelle Wartezeit-Statistik je Station (vom Server), für die Anzeige beim Bestellen. */
export const waitStats = signal<Record<string, { avgMs: number; count: number }>>({});

/** Verkaufte Mengen je Artikel (vom Server) — Grundlage für den Bestand. */
export const soldCounts = signal<Record<string, number>>({});
/** Tickt minütlich, damit Datums-/Uhrzeit-Verfügbarkeit neu bewertet wird. */
export const now = signal(Date.now());
/** Ab dieser Restmenge wird in der Kellner-App gewarnt ("bald aus"). */
export const LOW_STOCK = 10;

/** Restmenge eines Artikels (stock − verkauft); null = unbegrenzt. */
export function remainingOf(item: MenuItem): number | null {
  return item.stock == null ? null : item.stock - (soldCounts.value[item.id] ?? 0);
}
/** Kann der Artikel jetzt bestellt werden? (verfügbar + im Zeitfenster + Rest > 0) */
export function isOrderable(item: MenuItem): boolean {
  if (!item.isAvailable) return false;
  if (!isWithinSchedule(item, new Date(now.value))) return false;
  const rem = remainingOf(item);
  return rem == null || rem > 0;
}

/** Gültige Tische (Bereiche + Sonderplätze). Leer = keine Einschränkung. */
export const tablesConfig = signal<TablesConfig>({ ranges: [], extras: [] });
/** Werden Tische überhaupt geprüft? */
export const tablesRestricted = computed(() => tablesConfigured(tablesConfig.value));
/** Ist ein Tisch-Label aktuell erlaubt? (ohne Konfig immer true) */
export function tableAllowed(label: string): boolean {
  return isTableAllowed(tablesConfig.value, label);
}

// Lazy: erst beim ersten Event konstruieren, damit getDeviceId() NACH hydrateIdentity() läuft
// (sonst würde der Modul-Import eine deviceId prägen, bevor die aus IndexedDB restauriert ist).
let _clock: HlcClock | null = null;
function clock(): HlcClock {
  return (_clock ??= new HlcClock(getDeviceId(), Date.now()));
}

export const draftTotalCents = computed(() =>
  draft.value ? Draft.draftTotalCents(draft.value) : cents(0),
);
export const unsentCount = computed(() => (draft.value ? Draft.unsentCount(draft.value) : 0));

export const itemsByActiveCategory = computed<MenuItem[]>(() => {
  const m = menu.value;
  const cat = activeCategory.value;
  if (!m || !cat) return [];
  return m.items.filter((i) => i.categoryId === cat).sort((a, b) => a.sortOrder - b.sortOrder);
});

/**
 * Blockzustand bei Venue-Wechsel: liegen noch nicht gesendete Bons der VORIGEN
 * Veranstaltung in der Outbox und lassen sie sich nicht drainen (offline), wird der
 * Wechsel blockiert statt die Bons an die neue Venue fehlzuleiten. null = kein Block.
 */
export const venueSwitchBlock = signal<{ count: number } | null>(null);

/**
 * Venue-Wechsel sauber behandeln (Handy wird für eine andere Veranstaltung genutzt):
 * sync-before-wipe. Gibt "blocked" zurück, wenn noch un-gesynchte Bons der alten Venue
 * offen sind und nicht hochgeschoben werden können — dann NICHT wipen.
 */
async function reconcileVenue(v: VenueId): Promise<"ok" | "blocked"> {
  const stateVenue = await kvGet<string>(STATE_VENUE_KEY);
  const action = venueAction(stateVenue, v);
  if (action === "init") {
    // Erststart bzw. Bestand vor diesem Feature: Marker setzen, NICHT wipen.
    await kvSet(STATE_VENUE_KEY, v);
    return "ok";
  }
  if (action === "ok") return "ok";

  // --- Venue-Wechsel erkannt (action === "switch") ---
  // 1) Sync-before-wipe: Outbox der ALTEN Venue leeren (Wrapper/Auth = alte Venue, deren
  //    Push-Key ist noch gecacht; der Relay speichert ohnehin je Event-eigener venueId).
  let still = (await pending()).length;
  if (still > 0) {
    await flush(stateVenue);
    still = (await pending()).length;
  }
  if (still > 0) {
    venueSwitchBlock.value = { count: still }; // offline → blocken, nicht fehlleiten
    return "blocked";
  }
  // 2) Event-bezogenen Stand der alten Venue wegräumen (stateVenue ist hier gesetzt).
  await wipeVenueState(stateVenue!);
  // 3) Marker auf die neue Venue.
  await kvSet(STATE_VENUE_KEY, v);
  venueSwitchBlock.value = null;
  return "ok";
}

/** Lokalen, venue-bezogenen Stand löschen. NUR aufrufen, wenn die Outbox leer ist. */
async function wipeVenueState(oldVenue: string): Promise<void> {
  await outboxClear(); // defensiv (hier bereits leer)
  await ordersClear(); // "Meine Bons" der alten Venue
  for (const k of await kvKeys()) {
    if (isVenueScopedKey(k)) await kvDelete(k);
  }
  // In-Memory zurücksetzen + Kellner der alten Venue abmelden (Re-Login für neue Venue).
  await resetMirror(oldVenue as VenueId); // Cloud-Mirror der alten Venue verwerfen
  draft.value = null;
  myOrders.value = [];
  menu.value = null;
  logout();
  console.info(`Venue-Wechsel: lokaler Stand von ${oldVenue.slice(0, 8)} verworfen.`);
}

/** Beim Start: Karte aus Cache laden, dann (online) aktualisieren; Bon-Historie laden. */
export async function initStore(): Promise<void> {
  menuLoading.value = true;
  await loadVenue(); // Venue vom Server beziehen (zero-config); offline gilt der Cache
  const v = settings.value.venueId;
  // Venue-Wechsel sauber abfangen, BEVOR irgendetwas der neuen Venue geladen wird.
  if ((await reconcileVenue(v)) === "blocked") {
    menuLoading.value = false;
    return;
  }
  await initMirror(v); // Cloud-Event-Mirror dieser Venue laden (Mobilfunk-Lesepfad)
  startMirrorReactivity();
  const cached = await loadCachedMenu(v);
  if (cached) setMenu(cached);
  const fresh = await refreshMenu(settings.value.serverUrl, v);
  if (fresh) setMenu(fresh);
  // Lokaler Server nicht erreichbar → Cloud-Fallback versuchen, UNABHÄNGIG davon, ob (und wie
  // alt) bereits etwas gecacht ist. Sonst bleibt ein Gerät, das je irgendeine Karte gecacht
  // hatte, für immer auf dem alten Stand hängen, sobald es dauerhaft keinen lokalen Server
  // mehr erreicht (Cloud wäre erreichbar, wird aber nie gefragt) — auch cloudFallback/pushKey
  // würden nie bezogen, obwohl Internet da ist.
  else await loadCloudBootstrap(v);
  menuLoading.value = false;
  await loadMyOrders();
  await restoreCurrentDraft();
  void loadWait();
  void loadSold();
  void loadTables();
  startMenuAutoRefresh();
}

/** So lange gelten zuletzt geholte Wartezeiten höchstens (== Server-Fenster). */
const WAIT_STATS_TTL_MS = 30 * 60_000;
/** Zeitpunkt des letzten erfolgreichen Wait-Abrufs (0 = noch keiner). */
let lastWaitOkMs = 0;

/**
 * Voll-Cloud-Modus: kein lokaler Server erreichbar → Menü + Personal + push-Token direkt
 * von der Cloud holen (kasse-api/bootstrap.php, same-origin). Login/Bestellen/Push laufen
 * dann rein über die Cloud.
 */
/**
 * Relay-Basis im Cloud-Modus: Wird die PWA unter `kasse-<host>` ausgeliefert (eigener
 * Cloud-vhost), liegen die PHP-Endpunkte unter `<host>` → "kasse-"-Präfix abstreifen.
 * Sonst (z. B. `webon.plweb.at/kasse/`) ist die Relay-Basis die eigene Origin.
 */
export function relayBase(): string {
  const h = location.hostname;
  return h.startsWith("kasse-") ? `${location.protocol}//${h.slice(6)}` : location.origin;
}

async function loadCloudBootstrap(v: VenueId): Promise<void> {
  if (deviceUnassigned.value) return; // nicht zugewiesen → kein Personal/Karte der alten Venue laden
  const base = relayBase();
  try {
    const res = await fetch(`${base}/kasse-api/bootstrap.php?venue=${encodeURIComponent(v)}`);
    if (!res.ok) {
      cloudOnline.value = false;
      return;
    }
    const data = (await res.json()) as {
      menu: MenuSnapshot | null;
      users: User[];
      tables?: TablesConfig | null;
      readKey?: string | null;
    };
    cloudOnline.value = true; // Cloud ist erreichbar (wir lesen gerade von ihr)
    // Nur bei Versionswechsel setzen, sonst springt alle 30 s die Kategorie-Auswahl.
    if (data.menu && data.menu.version !== menu.value?.version) setMenu(data.menu);
    if (Array.isArray(data.users)) await setUsers(data.users);
    // Tischplan (Config) über die Cloud — im Mobilfunk-Betrieb sonst nicht verfügbar.
    if (data.tables) {
      tablesConfig.value = data.tables;
      void kvSet("tablesConfig", data.tables);
    }
    // Relay-URL (+readKey) merken → Cloud-Push/Status möglich. Schreib-Auth ist der Geräte-Token.
    setCloudFallback(base, data.readKey ?? undefined);
  } catch {
    cloudOnline.value = false; // auch Cloud nicht erreichbar
  }
}

/** Venue dieses Servers übernehmen (ein Server = eine Veranstaltung). Offline: Cache gilt. */
async function loadVenue(): Promise<void> {
  if (venuePinned) return; // per URL (?venue=) fest vorgegeben
  try {
    const res = await fetch(`${settings.value.serverUrl}/api/venue`);
    if (!res.ok) return;
    const { venueId } = (await res.json()) as { venueId: VenueId | null };
    if (venueId && venueId !== settings.value.venueId) updateSettings({ venueId });
  } catch {
    /* offline – gecachte Venue genügt */
  }
}

async function loadWait(): Promise<void> {
  try {
    const res = await fetch(`${settings.value.serverUrl}/api/wait?venue=${settings.value.venueId}`);
    if (res.ok) {
      waitStats.value = (await res.json()) as Record<string, { avgMs: number; count: number }>;
      lastWaitOkMs = Date.now();
      return;
    }
  } catch {
    /* lokaler Server weg → Cloud-Mirror unten */
  }
  // Kein lokaler Server: aus dem Cloud-Mirror rechnen (Mobilfunk-Gleichwertigkeit).
  if (mirrorOrders.value.length > 0) {
    waitStats.value = computeWaitStats(mirrorOrders.value, Date.now());
    lastWaitOkMs = Date.now();
    return;
  }
  // Weder lokal noch Mirror: veraltete Werte nach Ablauf des Fensters verwerfen.
  if (Date.now() - lastWaitOkMs > WAIT_STATS_TTL_MS) waitStats.value = {};
}

/** Verkaufte Mengen holen (für den Bestand). Lokaler Server, sonst Cloud-Mirror. */
async function loadSold(): Promise<void> {
  try {
    const res = await fetch(`${settings.value.serverUrl}/api/sold?venue=${settings.value.venueId}`);
    if (res.ok) {
      soldCounts.value = (await res.json()) as Record<string, number>;
      return;
    }
  } catch {
    /* lokaler Server weg → Cloud-Mirror unten */
  }
  if (mirrorOrders.value.length > 0) soldCounts.value = computeSoldCounts(mirrorOrders.value);
}

/**
 * Gültige Tische laden. Quelle ist der lokale Server, wenn erreichbar; sonst der Cache — aber
 * NUR als Erstbefüllung (Kaltstart/Offline). Im Cloud-Modus hat loadCloudBootstrap tablesConfig
 * bereits (frischer) aus der Cloud gesetzt; ein blindes Zurückschreiben des Caches würde diese
 * Version wieder auf einen veralteten Stand ziehen (z. B. fehlender Sonderplatz) und alle 30 s
 * beim Tick erneut überschreiben.
 */
async function loadTables(): Promise<void> {
  const cur = tablesConfig.value;
  if (cur.ranges.length === 0 && cur.extras.length === 0) {
    const cached = await kvGet<TablesConfig>("tablesConfig");
    if (cached) tablesConfig.value = cached;
  }
  try {
    const res = await fetch(`${settings.value.serverUrl}/api/tables?venue=${settings.value.venueId}`);
    if (res.ok) {
      const cfg = (await res.json()) as TablesConfig;
      tablesConfig.value = cfg;
      void kvSet("tablesConfig", cfg);
    }
  } catch {
    /* offline – Cache genügt */
  }
}

/** Beim Start den zuletzt aktiven Bon wieder öffnen (überlebt Reload/Browser-Neustart). */
async function restoreCurrentDraft(): Promise<void> {
  const id = await kvGet<string>("currentDraftId");
  if (!id) return;
  const rec = myOrders.value.find((r) => r.id === id);
  if (rec && rec.serverStatus !== "paid" && rec.serverStatus !== "void" && !isFullyPaid(Draft.payableOf(rec.draft))) {
    draft.value = rec.draft;
    tableView.value = "order";
  }
}

/**
 * Cloud-Mirror reaktiv an die Anzeige koppeln: zieht der Mirror (via sync-status) neue Events,
 * werden Bon-Status/-Nummer, verkaufte Mengen und Wartezeiten sofort neu berechnet — aber nur
 * im Mobilfunk-Betrieb. Im LAN führen die schnellen Server-Endpunkte (nicht überschreiben).
 */
let mirrorReactive = false;
function startMirrorReactivity(): void {
  if (mirrorReactive) return;
  mirrorReactive = true;
  mirrorOrders.subscribe((orders) => {
    if (localOnline.value || orders.length === 0) return;
    enrichFrom(orders);
    soldCounts.value = computeSoldCounts(orders);
    waitStats.value = computeWaitStats(orders, Date.now());
  });
}

/** Karte im Betrieb aktuell halten: Versionsabgleich periodisch + bei Reconnect/Fokus. */
let autoRefreshStarted = false;
function startMenuAutoRefresh(): void {
  if (autoRefreshStarted) return;
  autoRefreshStarted = true;
  const tick = () => {
    now.value = Date.now(); // Datums-/Uhrzeit-Verfügbarkeit neu bewerten
    // Keiner Veranstaltung zugewiesen → NICHT syncen (sonst lädt loadCloudBootstrap Personal/
    // Karte der alten Venue nach, u. a. beim Resume/visibilitychange).
    if (deviceUnassigned.value) return;
    void refreshMenuNow(); // versucht lokal zuerst, fällt intern auf die Cloud zurück
    void loadWait();
    void loadSold();
    void loadTables();
  };
  window.setInterval(tick, 30_000);
  window.addEventListener("online", tick);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") tick();
  });
}

async function refreshMenuNow(): Promise<void> {
  const fresh = await refreshMenu(settings.value.serverUrl, settings.value.venueId);
  if (fresh) {
    if (fresh.version !== menu.value?.version) setMenu(fresh);
  } else {
    // Lokaler Server gerade nicht erreichbar → Cloud-Fallback versuchen (aktualisiert Menü/
    // Personal/Push-Key, wenn Internet da ist). Läuft bei jedem Tick, nicht nur beim Kaltstart —
    // sonst bleibt ein Gerät, das lange keinen lokalen Server sieht, auf altem Stand hängen.
    await loadCloudBootstrap(settings.value.venueId);
  }
}

function setMenu(m: MenuSnapshot): void {
  menu.value = m;
  const cats = [...m.categories].sort((a, b) => a.sortOrder - b.sortOrder);
  if (!activeCategory.value || !cats.some((c) => c.id === activeCategory.value)) {
    activeCategory.value = cats[0]?.id ?? null;
  }
}

// ---- Bon-Historie ------------------------------------------------------------

export async function loadMyOrders(): Promise<void> {
  const recs = await ordersGetAll<OrderRecord>();
  // Nur echte Bons (mind. eine Position oder schon gesendet) — keine leeren Entwürfe.
  myOrders.value = recs
    .map((r) => {
      r.draft.payments = r.draft.payments ?? []; // Abwärtskompat. für ältere Datensätze
      return r;
    })
    // Nur Bons der aktuellen Venue (Defense-in-depth gegen Alt-Mischstand vor dem Reset).
    .filter((r) => r.draft.venueId === settings.value.venueId)
    .filter((r) => r.draft.sentOnce || r.draft.lines.length > 0)
    .sort((a, b) => b.updatedAtMs - a.updatedAtMs);
  void enrichFromServer();
}

/** Best-effort: Bon-Nummer/Status nachtragen — lokaler Server, sonst Cloud-Mirror. */
async function enrichFromServer(): Promise<void> {
  try {
    const res = await fetch(`${settings.value.serverUrl}/api/orders?venue=${settings.value.venueId}`);
    if (res.ok) {
      enrichFrom((await res.json()) as Order[]);
      return;
    }
  } catch {
    /* lokaler Server weg → Cloud-Mirror unten */
  }
  // Mobilfunk-Betrieb: Status + Bon-Nummer (order.numbered) aus dem Cloud-Mirror.
  if (mirrorOrders.value.length > 0) enrichFrom(mirrorOrders.value);
}

/** myOrders mit Server-/Mirror-Ständen anreichern (Status + Bon-Nummer). */
function enrichFrom(orders: Order[]): void {
  const map = new Map<string, Order>(orders.map((o) => [o.id, o]));
  myOrders.value = myOrders.value.map((r) => {
    const o = map.get(r.id);
    if (!o) return r;
    return { ...r, serverStatus: o.status, ...(o.seqNo !== undefined ? { seqNo: o.seqNo } : {}) };
  });
}

/** Draft setzen UND persistieren (überlebt Reloads, erscheint in "Meine Bons"). */
function setDraft(d: Draft.Draft): void {
  draft.value = d;
  void kvSet("currentDraftId", d.orderId); // für Wiederherstellung nach Reload
  // Leere Entwürfe nicht persistieren/zählen (sonst wächst "Meine Bons" bei jedem Tisch).
  if (d.lines.length === 0 && !d.sentOnce) return;
  const now = Date.now();
  const existing = myOrders.value.find((r) => r.id === d.orderId);
  const rec: OrderRecord = {
    id: d.orderId,
    draft: d,
    openedAtMs: existing?.openedAtMs ?? now,
    updatedAtMs: now,
    ...(existing?.seqNo !== undefined ? { seqNo: existing.seqNo } : {}),
    ...(existing?.serverStatus ? { serverStatus: existing.serverStatus } : {}),
  };
  void ordersPut(rec);
  myOrders.value = [rec, ...myOrders.value.filter((r) => r.id !== d.orderId)];
}

// ---- Bestell-Aktionen --------------------------------------------------------

export function startOrder(tableLabel: string): void {
  const u = currentUser.value;
  if (!u) return; // Anmeldung ist Pflicht
  setDraft(Draft.createDraft(settings.value.venueId, tableLabel as TableId, u.id));
  tableView.value = "order";
  screen.value = "order";
}

export function addItem(
  item: MenuItem,
  mods: OrderLineModifier[] = [],
  variant: SizeVariant | null = null,
  note = "",
): void {
  const d = draft.value;
  if (!d || isFullyPaid(Draft.payableOf(d))) return; // abgeschlossener Tisch: nichts mehr hinzufügen
  if (!isOrderable(item)) return; // nicht verfügbar / außerhalb Zeitfenster / ausverkauft
  setDraft(Draft.addLine(d, item, mods, note, variant));
}

/** Notiz an einer noch nicht gesendeten Bonzeile (nachträglich) setzen oder leeren. */
export function updateLineNote(lineId: OrderLineId, note: string): void {
  const d = draft.value;
  if (!d || isFullyPaid(Draft.payableOf(d))) return;
  setDraft(Draft.updateLineNote(d, lineId, note));
}

/** Größe/Beilage/Extras + Notiz einer noch nicht gesendeten Bonzeile nachträglich ändern. */
export function updateLineOptions(
  lineId: OrderLineId,
  item: MenuItem,
  variant: SizeVariant | null,
  mods: OrderLineModifier[],
  note: string,
): void {
  const d = draft.value;
  if (!d || isFullyPaid(Draft.payableOf(d))) return;
  setDraft(Draft.updateLineOptions(d, lineId, item, variant, mods, note));
}

/** Aktuelle Bestellung freigeben — der Tisch ist erledigt (z. B. nach Vollzahlung). */
export function closeActiveTable(): void {
  draft.value = null;
  void kvSet("currentDraftId", ""); // beim nächsten Start nicht wiederherstellen
  tableView.value = "pad";
}

export function changeQty(lineId: OrderLineId, delta: number): void {
  const d = draft.value;
  if (!d || isFullyPaid(Draft.payableOf(d))) return; // abgeschlossener Tisch: nicht editierbar
  setDraft(Draft.changeQty(d, lineId, delta));
}

export function voidLine(lineId: OrderLineId): void {
  if (draft.value) setDraft(Draft.voidLine(draft.value, lineId));
}

/** Zur Tischauswahl wechseln — der aktuelle Bon bleibt erhalten (nicht verworfen). */
export function backToTables(): void {
  tableView.value = "pad";
}

/** Zur laufenden Bestellung zurück. Ein (zwischenzeitlich) vollbezahlter Tisch wird geschlossen. */
export function resumeOrder(): void {
  const d = draft.value;
  if (!d) return;
  if (isFullyPaid(Draft.payableOf(d))) {
    closeActiveTable();
    return;
  }
  tableView.value = "order";
}

/** Eine gespeicherte Bestellung wieder öffnen (weiter bestellen). Bezahlte sind gesperrt. */
export function reopenOrder(rec: OrderRecord): void {
  if (isFullyPaid(Draft.payableOf(rec.draft))) return; // abgeschlossen → nicht bearbeitbar
  draft.value = rec.draft;
  void kvSet("currentDraftId", rec.id);
  tableView.value = "order";
  screen.value = "order";
}

/** Tischnummer der aktuellen Bestellung korrigieren (ohne neue Bestellung zu beginnen). */
export function changeTable(label: string): void {
  const d = draft.value;
  if (!d) return;
  setDraft({ ...d, tableRef: label as TableId });
  // Falls die Bestellung schon am Server ist: Korrektur als Event nachziehen.
  if (d.createdSent) {
    const env: EventEnvelope = {
      id: newId() as EventId,
      venueId: d.venueId,
      deviceId: getDeviceId(),
      hlc: clock().now(Date.now()),
      body: { kind: "table.changed", orderId: d.orderId, tableRef: label as TableId },
    };
    void enqueue([env]);
    void flush();
  }
}

/** Abrechnung öffnen. Gesendet wird erst beim Kassieren oder per „Senden" (sendAll). */
export function openPayment(): void {
  if (!draft.value) return;
  tableView.value = "pay";
}

/** Aus der Abrechnung zurück zur Bestellung (weitere Artikel hinzufügen). */
export function backToOrder(): void {
  tableView.value = "order";
}

/**
 * „Senden" vor dem Kassieren: alle ungesendeten Positionen gehen an die Stationen (Bons drucken
 * sofort), kassiert wird später. Gesendete Positionen sind danach gesperrt.
 */
export async function sendAll(): Promise<void> {
  const d = draft.value;
  if (!d) return;
  const { events, nextDraft } = Draft.sendLines(d, {
    mkEventId: () => newId() as EventId,
    clock: clock(),
    wallMs: Date.now(),
    deviceId: getDeviceId(),
  });
  if (events.length === 0) return;
  await enqueue(events);
  setDraft(nextDraft);
  void flush();
}

/**
 * (Teil-)Zahlung erfassen = Beleg erstellen. `items` listet bezahlte Mengen je Zeile.
 * Erzeugt ein payment.recorded-Event (Outbox → Server vergibt die Belegnummer).
 */
export async function recordPayment(
  items: { lineId: OrderLineId; qty: number }[],
  method: PaymentMethod,
): Promise<void> {
  const d = draft.value;
  if (!d || items.length === 0) return;
  const { events, nextDraft } = Draft.applyPayment(d, items, method, {
    mkEventId: () => newId() as EventId,
    mkLineId: () => newId() as OrderLineId,
    mkPaymentId: () => newId() as PaymentId,
    clock: clock(),
    wallMs: Date.now(),
    deviceId: getDeviceId(),
  });
  if (events.length === 0) return;
  await enqueue(events);
  setDraft(nextDraft);
  void flush();
  // Vollständig bezahlt → Tisch schließen, damit nichts mehr hinzugefügt werden kann.
  if (isFullyPaid(Draft.payableOf(nextDraft))) closeActiveTable();
}

// ---- Admin-Verwaltung in der App (rollengegated, ohne Account-Login) --------------------
// Änderungen laufen über DIESELBE Outbox/den Geräte-Token wie Bestellungen. Der Server löst
// Konflikte per LWW über die Version (höchste `version` je configKind gewinnt), daher muss die
// neue Version echt größer als der aktuelle Stand sein.

/** Beliebigen Konfig-Snapshot (menu/users/tables/…) als Event schreiben. */
export function writeConfigSnapshot(configKind: ConfigKind, data: unknown, version: number): void {
  const env: EventEnvelope = {
    id: newId() as EventId,
    venueId: settings.value.venueId as VenueId,
    deviceId: getDeviceId(),
    hlc: clock().now(Date.now()),
    body: { kind: "config.snapshot", configKind, version, data, atMs: Date.now() },
  };
  void enqueue([env]).then(() => flush());
}

/**
 * Speisekarte speichern (in der App bearbeiten wir nur Verfügbarkeit/Menge): neue Version
 * vergeben, optimistisch anzeigen und als menu-Snapshot schreiben.
 */
export function saveMenu(next: MenuSnapshot): void {
  const version = (menu.value?.version ?? 0) + 1;
  const updated: MenuSnapshot = { ...next, version };
  menu.value = updated; // optimistisch — der Sync bestätigt/überschreibt beim nächsten Refresh
  writeConfigSnapshot("menu", updated, version);
}

/** Nachdruck eines Bons/Belegs auslösen: neues print.requested(reprintOf) über die Outbox. */
export function requestReprint(ev: PrintRequested): void {
  const env: EventEnvelope = {
    id: newId() as EventId,
    venueId: settings.value.venueId as VenueId,
    deviceId: getDeviceId(),
    hlc: clock().now(Date.now()),
    body: {
      kind: "print.requested",
      orderId: ev.orderId,
      printJobId: crypto.randomUUID(),
      printer: ev.printer,
      doc: ev.doc,
      atMs: Date.now(),
      reprintOf: ev.printJobId,
    },
  };
  void enqueue([env]).then(() => flush());
}
